import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Building2, Users, Handshake, CheckSquare, BookOpen, X,
} from "lucide-react";
import { Input } from "./ui/input";
import { trpc } from "@/lib/trpc";
import { cn, getStageLabel } from "@/lib/utils";

type ResultType = "all" | "account" | "contact" | "deal" | "task" | "playbook";

type Result = {
  type: "account" | "contact" | "deal" | "task" | "playbook";
  id: number;
  name: string;
  subtitle?: string;
  accountId?: number | null;
  dealId?: number | null;
};

const TYPE_META: Record<Result["type"], { icon: typeof Building2; label: string; color: string }> = {
  account: { icon: Building2, label: "Account", color: "text-blue-400" },
  contact: { icon: Users, label: "Contact", color: "text-violet-400" },
  deal: { icon: Handshake, label: "Deal", color: "text-green-400" },
  task: { icon: CheckSquare, label: "Task", color: "text-amber-400" },
  playbook: { icon: BookOpen, label: "Playbook", color: "text-cyan-400" },
};

const FILTER_OPTIONS: { value: ResultType; label: string }[] = [
  { value: "all", label: "All" },
  { value: "account", label: "Accounts" },
  { value: "contact", label: "Contacts" },
  { value: "deal", label: "Deals" },
  { value: "task", label: "Tasks" },
  { value: "playbook", label: "Playbooks" },
];

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ResultType>("all");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const debounced = useDebounced(query.trim(), 180);

  const search = trpc.search.global.useQuery(
    { query: debounced },
    { enabled: debounced.length >= 2, staleTime: 10_000 }
  );

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        wrapRef.current?.querySelector("input")?.focus();
        setOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const allResults: Result[] = [];
  const d = search.data;
  if (d) {
    if (filter === "all" || filter === "account") allResults.push(...d.accounts);
    if (filter === "all" || filter === "deal") allResults.push(...d.deals);
    if (filter === "all" || filter === "contact") allResults.push(...d.contacts);
    if (filter === "all" || filter === "task") allResults.push(...d.tasks);
    if (filter === "all" || filter === "playbook") allResults.push(...d.playbooks);
  }
  const results = allResults.slice(0, 6);

  const go = (r: Result) => {
    setOpen(false);
    setQuery("");
    if (r.type === "account") navigate(`/accounts/${r.id}`);
    else if (r.type === "contact") navigate(`/contacts/${r.id}`);
    else if (r.type === "deal") navigate(`/deals/${r.id}`);
    else if (r.type === "task") navigate(`/tasks`);
    else if (r.type === "playbook") navigate(`/academy/${r.id}`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && results[activeIdx]) {
      e.preventDefault();
      go(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => { setActiveIdx(0); }, [debounced, filter]);

  return (
    <div ref={wrapRef} className="relative w-full max-w-xl">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search accounts, deals, contacts, tasks, playbooks…  ⌘K"
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown}
          className="pl-9 pr-9 h-9"
        />
        {query && (
          <button
            onClick={() => { setQuery(""); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && debounced.length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border bg-popover shadow-lg overflow-hidden">
          <div className="flex gap-1 border-b px-2 py-1.5 overflow-x-auto">
            {FILTER_OPTIONS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={cn(
                  "text-xs px-2 py-0.5 rounded-md whitespace-nowrap transition-colors",
                  filter === f.value
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {search.isFetching && (
              <div className="px-3 py-3 text-xs text-muted-foreground">Searching…</div>
            )}
            {!search.isFetching && results.length === 0 && (
              <div className="px-3 py-3 text-xs text-muted-foreground">No results</div>
            )}
            {results.map((r, i) => {
              const meta = TYPE_META[r.type];
              const Icon = meta.icon;
              return (
                <button
                  key={`${r.type}-${r.id}`}
                  onClick={() => go(r)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 text-left transition-colors",
                    activeIdx === i ? "bg-accent" : "hover:bg-accent/50"
                  )}
                >
                  <Icon className={cn("h-4 w-4 shrink-0", meta.color)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{r.name}</p>
                    {r.subtitle && (
                      <p className="text-xs text-muted-foreground truncate">
                        {r.type === "deal" ? getStageLabel(r.subtitle) : r.subtitle}
                      </p>
                    )}
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground shrink-0">
                    {meta.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
